"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Phone, Mail, MapPin, Calendar as CalendarIcon, Wallet, Send, Loader2, CheckCircle2, Headset, HelpCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { RangeInput } from "@/components/ui/range-input";
const SHARED_PHONE = "+84 24 3789 1234";
const SHARED_EMAIL = "contact@av-pro.vn";
const SHARED_ADDRESS = "Tòa nhà Technopark, Gia Lâm, Hà Nội, Việt Nam";
export default function GetInTouch() {
  return <section data-section-id="26106" id="contact-us" className="relative w-full py-20 lg:py-32 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569116/img/middle_east_topography_bg.png)] bg-cover bg-center bg-no-repeat opacity-[0.02] dark:opacity-[0.05]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="flex flex-col gap-8">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tighter text-primary uppercase">
                Liên Hệ Tư Vấn
              </h2>
              <p className="text-lg font-sans text-muted-foreground max-w-md leading-relaxed">
                Sẵn sàng nâng tầm trải nghiệm AV của bạn? Đội ngũ kỹ sư tại Hà Nội của chúng tôi đã sẵn sàng thiết lập nền tảng kỹ thuật cho sự thành công của bạn.
              </p>
            </div>

            <div className="grid gap-6">
              {}
              <Card className="bg-card/40 backdrop-blur-sm border-border rounded-none shadow-sm hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-none bg-primary text-primary-foreground">
                      <HelpCircle className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-default font-bold text-primary">Yêu Cầu Chung</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className="text-muted-foreground font-sans">
                    Đối với các câu hỏi về dịch vụ tư vấn, giải pháp thị trường hoặc quan hệ đối tác chiến lược.
                  </p>
                  <Link to={`mailto:${SHARED_EMAIL}`} className="flex items-center gap-2 text-foreground hover:text-accent transition-colors font-mono font-medium w-fit">
                    <Mail className="size-4 text-primary" />
                    {SHARED_EMAIL}
                  </Link>
                </CardContent>
              </Card>

              {}
              <Card className="bg-card/40 backdrop-blur-sm border-border rounded-none shadow-sm hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-none bg-secondary text-secondary-foreground">
                      <Headset className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-default font-bold text-primary">Hỗ Trợ Kỹ Thuật</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className="text-muted-foreground font-sans">
                    Đường dây trực tiếp dành cho khách hàng hiện tại cần hỗ trợ kỹ thuật khẩn cấp hoặc cập nhật dự án.
                  </p>
                  <Link to={`tel:${SHARED_PHONE.replace(/\s/g, '')}`} className="flex items-center gap-2 text-foreground hover:text-accent transition-colors font-mono font-medium w-fit">
                    <Phone className="size-4 text-primary" />
                    {SHARED_PHONE}
                  </Link>
                </CardContent>
              </Card>

              {}
              <div className="flex items-start gap-3 p-4 rounded-none border border-border/50 bg-muted/20">
                <MapPin className="size-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-default font-bold text-sm text-primary uppercase tracking-wider">Trụ Sở Chính</h4>
                  <p className="text-sm font-sans text-muted-foreground">{SHARED_ADDRESS}</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card border border-border rounded-none p-6 md:p-8 shadow-2xl relative overflow-hidden backdrop-blur-md">
            {}
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
            
            <div className="mb-8 relative z-10">
              <h3 className="text-2xl font-default font-bold text-primary mb-2">Bắt Đầu Dự Án</h3>
              <p className="text-muted-foreground font-sans text-sm">
                Điền vào mẫu bên dưới và chúng tôi sẽ phản hồi lại bạn với một đề xuất kỹ thuật tối ưu.
              </p>
            </div>

            <FormProvider formKey="contact_form_project1project2andproject3" formId="contact_form_project1project2andproject3" sectionName="contact-us-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            message: "",
            budget: 50000000,
            timeline: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            },
            timeline: {
              required: true
            }
          }} submitText="Gửi Yêu Cầu" successMessage="Cảm ơn bạn! Yêu cầu của bạn đã được tiếp nhận. Chúng tôi sẽ liên hệ sớm nhất.">
              <ContactForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [budget, setBudget] = useState(50000000);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-none bg-accent/10 flex items-center justify-center mb-2">
          <CheckCircle2 className="size-8 text-accent" />
        </div>
        <h4 className="text-xl font-default font-bold text-primary uppercase">Đã Gửi Thành Công!</h4>
        <p className="text-muted-foreground font-sans max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4 rounded-none border-primary text-primary font-default font-bold" onClick={() => window.location.reload()}>
          Gửi Tin Nhắn Khác
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold">Họ và Tên <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Nguyễn Văn A" className="rounded-none border-border focus-visible:ring-accent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold">Địa Chỉ Email <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@company.vn" className="rounded-none border-border focus-visible:ring-accent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold">Số Điện Thoại</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+84 ..." className="rounded-none border-border focus-visible:ring-accent" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-mono">Tùy chọn – bao gồm mã vùng</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold">Công Ty / Tổ Chức</FormLabel>
              <FormControl>
                <Input placeholder="Tên doanh nghiệp" className="rounded-none border-border focus-visible:ring-accent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
        <FormField control={control} name="timeline" render={({
        field
      }) => <FormItem className="flex flex-col">
              <FormLabel className="flex items-center gap-2 text-primary font-default font-semibold">
                <SafeHydrate><CalendarIcon className="size-3.5 text-primary" /></SafeHydrate>
                Thời Gian Dự Kiến <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="date" className="w-full block rounded-none border-border focus-visible:ring-accent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center justify-between w-full text-primary font-default font-semibold">
                <span className="flex items-center gap-2">
                  <Wallet className="size-3.5 text-primary" />
                  Ngân Sách (VNĐ)
                </span>
                <span className="text-accent font-mono font-bold text-sm">
                  {budget.toLocaleString()}đ
                </span>
              </FormLabel>
              <FormControl>
                <div className="pt-4 px-1">
                  <RangeInput min={10000000} max={500000000} step={10000000} value={field.value || 50000000} onValueChange={val => {
              setBudget(val);
              field.onChange(val);
            }} className="accent-accent" />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-2 font-mono">
                    <span>10Tr</span>
                    <span>500Tr+</span>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-default font-semibold">Nội Dung Yêu Cầu <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-none border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-50 font-sans" placeholder="Mô tả chi tiết về nhu cầu hệ thống AV của bạn..." {...field} />
            </FormControl>
            <FormDescription className="font-sans">Cung cấp càng nhiều chi tiết càng tốt để chúng tôi tư vấn chính xác nhất.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-2">
          <span className="text-destructive text-sm font-bold">Lỗi:</span>
          <p className="text-destructive text-sm font-sans">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-bold mt-2 bg-accent text-accent-foreground rounded-none hover:bg-accent/90 shadow-lg">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Đang Gửi Yêu Cầu...
          </> : <>
            GỬI TIN NHẮN
            <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}